import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Compass, Hammer, Ruler, Layers } from "lucide-react";
import { HomeHero } from "@/components/site/HomeHero";
import { Marquee } from "@/components/site/Marquee";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { Frame } from "@/components/site/Frame";
import { CTA } from "@/components/site/CTA";
import { SERVICES, PROJECTS, PRINCIPLES, MARQUEE } from "@/data/content";

const Intro = () => (
  <section data-testid="home-intro" className="mx-auto max-w-[1440px] px-5 sm:px-10 py-28 sm:py-40 grid lg:grid-cols-12 gap-12">
    <Reveal className="lg:col-span-4">
      <Eyebrow>Who we are</Eyebrow>
    </Reveal>
    <Reveal delay={0.1} className="lg:col-span-8">
      <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium leading-[1.15] tracking-tight">
        A Preston studio joining <span className="text-[#d96b27]">architectural thinking</span> with hands-on building —
        so the drawing and the build <span className="outline-text">never drift apart.</span>
      </p>
      <div className="mt-12 grid sm:grid-cols-2 gap-8 text-[#a7aebb] leading-relaxed">
        <p>We carry out residential and commercial design and construction: extensions, loft conversions, refurbishment, conservatories and interior finishing.</p>
        <p>Building on that practical base, we take on complete projects and see them through from the first idea to the finished building.</p>
      </div>
    </Reveal>
  </section>
);

const Bento = () => {
  const cells = [
    { icon: Compass, title: "Architecture", text: "Concept to building regs, designed with the build in mind.", cls: "lg:col-span-2 lg:row-span-2", img: "/img/drafting.jpg" },
    { icon: Hammer, title: "Construction", text: "Extensions, lofts and refurbishment, sequenced properly.", cls: "" },
    { icon: Layers, title: "Interiors", text: "Joinery, kitchens and finishes people touch every day.", cls: "" },
    { icon: Ruler, title: "One team", text: "Design & Build: one point of contact from survey to handover.", cls: "lg:col-span-2" },
  ];
  return (
    <section data-testid="home-bento" className="mx-auto max-w-[1440px] px-5 sm:px-10 pb-28 sm:pb-40">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[260px] gap-px bg-[#262b38] border border-[#262b38]">
        {cells.map((c, i) => (
          <motion.div
            key={c.title}
            data-testid={`bento-cell-${i}`}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8, delay: i * 0.08 }}
            whileHover={{ backgroundColor: "#14171f" }}
            className={`relative overflow-hidden bg-[#0c0e12] p-8 flex flex-col justify-between min-h-[240px] ${c.cls}`}
          >
            {c.img && (
              <img src={c.img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 grayscale" />
            )}
            <c.icon className="relative w-7 h-7 text-[#d96b27]" strokeWidth={1.4} />
            <div className="relative">
              <h3 className={`font-display font-bold tracking-tight ${c.img ? "text-4xl sm:text-5xl" : "text-2xl"}`}>{c.title}</h3>
              <p className="mt-3 text-[#c9ced8] max-w-sm">{c.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const ServicesList = () => (
  <section data-testid="home-services" className="border-t border-[#262b38]">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-10 py-28 sm:py-40">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-16">
        <Reveal>
          <Eyebrow>Services</Eyebrow>
          <h2 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tight mt-6">What we do</h2>
        </Reveal>
        <Link to="/services" data-testid="home-all-services-link" className="font-mono text-xs uppercase tracking-[0.2em] text-[#a7aebb] hover:text-[#d96b27] flex items-center gap-2">
          All services <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
      <ul>
        {SERVICES.slice(0, 6).map((s, i) => (
          <Reveal key={s.id} delay={i * 0.04}>
            <Link
              to={`/services#${s.id}`}
              data-testid={`home-service-row-${s.id}`}
              className="group grid grid-cols-12 items-center gap-4 py-7 border-t border-[#262b38] last:border-b"
            >
              <span className="col-span-2 sm:col-span-1 font-mono text-xs text-[#a7aebb]">{s.code}</span>
              <span className="col-span-10 sm:col-span-5 font-display text-2xl sm:text-4xl font-bold tracking-tight transition-transform duration-500 group-hover:translate-x-3 group-hover:text-[#d96b27]">
                {s.title}
              </span>
              <span className="hidden sm:block sm:col-span-5 text-[#a7aebb]">{s.short}</span>
              <ArrowUpRight className="hidden sm:block col-span-1 justify-self-end w-6 h-6 text-[#a7aebb] transition-transform duration-500 group-hover:rotate-45 group-hover:text-[#d96b27]" />
            </Link>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

const Principles = () => (
  <section data-testid="home-principles" className="mx-auto max-w-[1440px] px-5 sm:px-10 py-28 sm:py-40 space-y-24 sm:space-y-36">
    {PRINCIPLES.map((p, i) => (
      <div key={p.n} className="grid lg:grid-cols-12 gap-10 items-center">
        <Reveal className={`lg:col-span-7 ${i % 2 ? "lg:order-2" : ""}`}>
          <Frame src={p.img} alt={p.title} className="aspect-[4/3]" label={`Principle ${p.n}`} testId={`principle-image-${p.n}`} />
        </Reveal>
        <Reveal delay={0.1} className={`lg:col-span-5 ${i % 2 ? "lg:order-1" : ""}`}>
          <span className="font-display text-8xl font-extrabold outline-text">{p.n}</span>
          <h3 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">{p.title}</h3>
          <p className="text-[#a7aebb] text-base md:text-lg mt-6 leading-relaxed max-w-md">{p.text}</p>
        </Reveal>
      </div>
    ))}
  </section>
);

const Featured = () => (
  <section data-testid="home-featured" className="border-t border-[#262b38] mx-auto max-w-[1440px] px-5 sm:px-10 py-28 sm:py-40">
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-16">
      <Reveal>
        <Eyebrow>Selected work</Eyebrow>
        <h2 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tight mt-6">Inside the box</h2>
      </Reveal>
      <Link to="/projects" data-testid="home-all-projects-link" className="font-mono text-xs uppercase tracking-[0.2em] text-[#a7aebb] hover:text-[#d96b27] flex items-center gap-2">
        All projects <ArrowUpRight className="w-4 h-4" />
      </Link>
    </div>
    <div className="grid lg:grid-cols-12 gap-6">
      {PROJECTS.slice(0, 4).map((p, i) => (
        <Reveal key={p.id} delay={(i % 2) * 0.1} className={p.span}>
          <Frame src={p.img} alt={p.title} className="aspect-[4/3] lg:aspect-auto lg:h-[460px]" testId={`featured-project-${p.id}`} />
          <div className="flex justify-between items-baseline mt-4">
            <h3 className="font-display text-xl sm:text-2xl font-bold">{p.title}</h3>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#a7aebb]">{p.category}</span>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <HomeHero />
      <Intro />
      <Bento />
      <Marquee items={MARQUEE} />
      <ServicesList />
      <Principles />
      <Featured />
      <CTA />
    </div>
  );
}
